import React from 'react';
import { MCPServer, MCPCapability } from '@/shared/types';
import { DynamicToolForm } from './DynamicToolForm';
import { CustomHeaders } from './CustomHeaders';

interface ServerFormProps {
  isEditing: boolean;
  formData: Partial<MCPServer>;
  onFormDataChange: (updates: Partial<MCPServer>) => void;
  onSave: () => void;
  onCancel: () => void;
  selectedServer: MCPServer | null;
  loading: boolean;
  // Tool execution props
  executingTool: any;
  onStartToolExecution: (tool: any) => void;
  onExecuteTool: (data: any) => void;
  onCancelToolExecution: () => void;
  executionResult: any;
}

export const ServerForm: React.FC<ServerFormProps> = ({
  isEditing,
  formData,
  onFormDataChange,
  onSave,
  onCancel,
  selectedServer,
  loading,
  executingTool,
  onStartToolExecution,
  onExecuteTool,
  onCancelToolExecution,
  executionResult,
}) => {
  return (
    <div className="flex-1 overflow-hidden flex h-full">
      {/* Left Panel - Server Configuration */}
      <div className="w-1/3 border-r border-gray-700 flex flex-col h-full">
        <div className="p-6 border-b border-gray-700 flex-shrink-0">
          <h3 className="text-lg font-semibold text-white mb-2">
            {isEditing ? 'Edit Server' : 'Add New Server'}
          </h3>
          <p className="text-sm text-gray-400">Configure server settings</p>
        </div>
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Server Name
              </label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => onFormDataChange({ name: e.target.value })}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter server name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => onFormDataChange({ description: e.target.value })}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter server description"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Server URL
              </label>
              <input
                type="url"
                value={formData.url || ''}
                onChange={(e) => onFormDataChange({ url: e.target.value })}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://api.example.com/mcp"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Authentication Type
              </label>
              <select
                value={formData.authType || 'none'}
                onChange={(e) => onFormDataChange({ 
                  authType: e.target.value as any,
                  authConfig: { ...formData.authConfig, customHeaders: formData.authConfig?.customHeaders || [] }
                })}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="none">None</option>
                <option value="api_key">API Key</option>
                <option value="oauth">OAuth</option>
                <option value="basic">Basic Auth</option>
                <option value="header">Single Header</option>
                <option value="custom_headers">Custom Headers</option>
              </select>
            </div>
            {formData.authType === 'api_key' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  API Key
                </label>
                <input
                  type="password"
                  value={formData.authConfig?.apiKey || ''}
                  onChange={(e) => onFormDataChange({ 
                    authConfig: { ...formData.authConfig, apiKey: e.target.value }
                  })}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter API key"
                />
              </div>
            )}
            {formData.authType === 'header' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Header Name
                  </label>
                  <input
                    type="text"
                    value={formData.authConfig?.headerName || ''}
                    onChange={(e) => onFormDataChange({ 
                      authConfig: { ...formData.authConfig, headerName: e.target.value }
                    })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="X-API-Key"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Header Value
                  </label>
                  <input
                    type="password"
                    value={formData.authConfig?.headerValue || ''}
                    onChange={(e) => onFormDataChange({ 
                      authConfig: { ...formData.authConfig, headerValue: e.target.value }
                    })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your-api-key-value"
                  />
                </div>
              </div>
            )}
            {formData.authType === 'custom_headers' && (
              <div>
                <CustomHeaders
                  headers={formData.authConfig?.customHeaders || []}
                  onChange={(customHeaders) => onFormDataChange({
                    authConfig: { ...formData.authConfig, customHeaders }
                  })}
                />
              </div>
            )}
            <div className="flex items-center gap-3 pt-4 pb-6">
              <button
                onClick={onSave}
                disabled={loading}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg font-medium transition-colors"
              >
                {loading ? 'Saving...' : 'Save Server'}
              </button>
              <button
                onClick={onCancel}
                className="px-6 py-3 bg-gray-600 hover:bg-gray-700 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Middle Panel - Available Tools */}
      <div className="w-1/3 border-r border-gray-700 flex flex-col h-full">
        <div className="p-6 border-b border-gray-700 flex-shrink-0">
          <h3 className="text-lg font-semibold text-white mb-2">Available Tools</h3>
          <p className="text-sm text-gray-400">Test and manage server tools</p>
        </div>
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          <div className="space-y-3">
            {(selectedServer?.capabilities || []).map((capability, index) => (
              <div key={index} className="bg-gray-800/50 rounded-xl p-4 border border-gray-700 hover:border-gray-600 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h5 className="text-base font-semibold text-white mb-1">{capability.name}</h5>
                    <p className="text-sm text-gray-400">{capability.description}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      capability.enabled ? 'bg-green-600 text-green-200' : 'bg-gray-600 text-gray-200'
                    }`}>
                      {capability.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <button
                      onClick={() => onStartToolExecution(capability)}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg font-medium transition-colors"
                      disabled={!capability.enabled}
                    >
                      Execute
                    </button>
                  </div>
                </div>
                {capability.parameters && capability.parameters.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-700">
                    <p className="text-xs text-gray-400 mb-2 font-medium">Parameters:</p>
                    <div className="space-y-1">
                      {capability.parameters.map((param, paramIndex) => (
                        <div key={paramIndex} className="flex items-center gap-2 text-xs">
                          <code className="text-blue-300 bg-gray-900 px-2 py-1 rounded">{param.name}</code>
                          <span className="text-gray-500">({param.type})</span>
                          {param.required && <span className="text-red-400 font-medium">*</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Tool Execution */}
      <div className="w-1/3 flex flex-col h-full">
        <div className="p-6 border-b border-gray-700 flex-shrink-0">
          <h3 className="text-lg font-semibold text-white mb-2">Tool Execution</h3>
          <p className="text-sm text-gray-400">
            {executingTool ? `Executing: ${executingTool.name}` : 'Select a tool to execute'}
          </p>
        </div>
        
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {executingTool ? (
            <div className="flex-1 overflow-y-auto p-6 min-h-0">
              <DynamicToolForm
                tool={executingTool}
                onExecute={onExecuteTool}
                onCancel={onCancelToolExecution}
                executionResult={executionResult}
              />
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h5 className="text-lg font-medium text-white mb-2">No Tool Selected</h5>
                <p className="text-gray-400">Choose a tool from the middle panel to execute it</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
